import { useCallback, useEffect, useRef } from 'react'

import { trackEvent } from 'services/analytics'

export const VIDEO_PROGRESS_MILESTONES = [25, 50, 75]

function roundSeconds(value) {
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue) || numberValue < 0) return 0
  return Math.round(numberValue)
}

function clampProgress(value) {
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return 0
  return Math.max(0, Math.min(100, Math.round(numberValue)))
}

function compactPayload(payload) {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc
    acc[key] = value
    return acc
  }, {})
}

export function normalizeVideoSnapshot(snapshot = {}) {
  const watchedSeconds = roundSeconds(snapshot.watchedSeconds)
  const durationSeconds = roundSeconds(snapshot.durationSeconds)
  const calculatedProgress = durationSeconds > 0
    ? (watchedSeconds / durationSeconds) * 100
    : snapshot.progressPercent

  return {
    watchedSeconds,
    durationSeconds,
    progressPercent: clampProgress(calculatedProgress),
  }
}

export function buildVideoAnalyticsPayload(metadata = {}, snapshot = {}, overrides = {}) {
  const normalizedSnapshot = normalizeVideoSnapshot({
    ...snapshot,
    progressPercent: overrides.progressPercent ?? snapshot.progressPercent,
  })

  return compactPayload({
    ...metadata,
    progress_percent: normalizedSnapshot.progressPercent,
    watched_seconds: normalizedSnapshot.watchedSeconds,
    duration_seconds: normalizedSnapshot.durationSeconds,
    ...overrides,
  })
}

export function createVideoAnalyticsSessionState() {
  const firedMilestones = new Set()
  let started = false
  let completed = false

  return {
    getProgressMilestones(progressPercent) {
      const normalizedProgress = clampProgress(progressPercent)
      const milestones = VIDEO_PROGRESS_MILESTONES.filter(milestone => (
        normalizedProgress >= milestone && !firedMilestones.has(milestone)
      ))

      milestones.forEach(milestone => firedMilestones.add(milestone))

      return milestones
    },
    hasCompleted() {
      return completed
    },
    hasStarted() {
      return started
    },
    shouldTrackCompleted() {
      if (completed) return false
      completed = true
      return true
    },
    shouldTrackStarted() {
      if (started) return false
      started = true
      return true
    },
  }
}

function logVideoAnalytics(eventName, payload) {
  if (process.env.NODE_ENV !== 'development') return

  console.log('[analytics-video]', eventName, {
    video_name: payload.video_name,
    video_type: payload.video_type,
    progress_percent: payload.progress_percent,
  })
}

export default function useVideoAnalytics({
  enabled = true,
  eventPrefix,
  metadata = {},
  sessionKey,
} = {}) {
  const sessionStateRef = useRef(createVideoAnalyticsSessionState())

  useEffect(() => {
    sessionStateRef.current = createVideoAnalyticsSessionState()
  }, [sessionKey])

  const sendEvent = useCallback((eventSuffix, snapshot, overrides = {}) => {
    if (!enabled || !eventPrefix) return

    const eventName = `${eventPrefix}_${eventSuffix}`
    const payload = buildVideoAnalyticsPayload(metadata, snapshot, overrides)

    logVideoAnalytics(eventName, payload)
    trackEvent(eventName, payload)
  }, [enabled, eventPrefix, metadata])

  const trackStarted = useCallback((snapshot = {}) => {
    if (!sessionStateRef.current.shouldTrackStarted()) return

    sendEvent('started', snapshot)
  }, [sendEvent])

  const trackProgress = useCallback((snapshot = {}) => {
    const normalizedSnapshot = normalizeVideoSnapshot(snapshot)
    const milestones = sessionStateRef.current.getProgressMilestones(normalizedSnapshot.progressPercent)

    milestones.forEach(milestone => {
      sendEvent('progress', normalizedSnapshot, { progress_percent: milestone })
    })
  }, [sendEvent])

  const trackCompleted = useCallback((snapshot = {}) => {
    if (!sessionStateRef.current.shouldTrackCompleted()) return

    sendEvent('completed', snapshot, { progress_percent: 100 })
  }, [sendEvent])

  const trackClosed = useCallback((snapshot = {}) => {
    if (!sessionStateRef.current.hasStarted() || sessionStateRef.current.hasCompleted()) return

    sendEvent('closed', snapshot)
  }, [sendEvent])

  return {
    trackClosed,
    trackCompleted,
    trackProgress,
    trackStarted,
  }
}
